import { useRef, type Ref, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";

import type { EmailAddress } from "@/api/bale.zod";

import {
  useCreateEmailAddress,
  useCreateEmailVerificationRequest,
  useDeleteEmailAddress,
  useListAccountEmailAddressesSuspense,
  useUpdateEmailAddress,
} from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { CardSubtitleHeader } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { ErrorDetail } from "@/components/ErrorDetail";
import { Failure } from "@/components/Failure";
import { Form } from "@/components/Form";
import { IconButton } from "@/components/IconButton";
import { Input } from "@/components/Input";
import { Success } from "@/components/Success";

export function ManageEmailAddressesModal({ ref, onClose }: { ref: Ref<HTMLDialogElement>; onClose?: () => void }) {
  const { t } = useTranslation();
  const form = useRef<HTMLFormElement>(null);
  const emailAddresses = useListAccountEmailAddressesSuspense("@me", {
    query: { select: (data) => data.data.email_addresses },
  });
  const createEmailAddress = useCreateEmailAddress({
    mutation: {
      onSuccess: () => form.current?.reset(),
    },
  });
  function handleCreate(event: SubmitEvent<HTMLFormElement>) {
    const form = new FormData(event.currentTarget);
    const email = form.get("email")! as string;
    createEmailAddress.mutate({ data: { email } });
  }

  return (
    <Dialog ref={ref} onClose={onClose} closeOnOutside className="w-2xl">
      <CardSubtitleHeader>{t("manage-email-addresses")}</CardSubtitleHeader>
      <div className="flex flex-col">
        <p className="p-4 pt-0 font-disinterest">{t("manage-email-addresses-description")}</p>
        <div className="flex flex-col border-t border-divider empty:hidden">
          {emailAddresses.data.map((email) => (
            <EmailItem key={email.id} email={email} />
          ))}
        </div>
        <Form
          ref={form}
          error={createEmailAddress.error}
          className="flex flex-row flex-wrap gap-x-4 gap-y-2 p-4"
          onSubmit={handleCreate}
        >
          <div className="grow-999">
            <Input name="email" type="email" placeholder={t("email-address")} />
          </div>
          <Button className="min-w-0 grow-1 truncate" type="submit">
            {t("add-email")}
          </Button>
        </Form>
      </div>
    </Dialog>
  );
}

function EmailItem({ email }: { email: EmailAddress }) {
  const { t } = useTranslation();
  const deleteEmail = useDeleteEmailAddress();
  const updateEmail = useUpdateEmailAddress();
  const createVerificationRequest = useCreateEmailVerificationRequest();

  return (
    <div className="flex flex-col gap-1 border-b border-divider px-4 py-2 text-start">
      <div className="flex flex-row items-center gap-2">
        <p className="font-interest">{email.email_address}</p>
        {email.is_verified ? (
          <Badge>{t("email-verified")}</Badge>
        ) : (
          <Badge attention="danger">{t("email-unverified")}</Badge>
        )}
        {email.is_primary && <Badge>{t("email-primary")}</Badge>}
        <ActionBox className="ml-auto">
          {!email.is_verified && (
            <IconButton
              type="button"
              name="shield"
              onClick={() => createVerificationRequest.mutate({ emailId: email.id })}
              disabled={createVerificationRequest.isSuccess}
            />
          )}
          {!email.is_primary && email.is_verified && (
            <IconButton
              type="button"
              name="star"
              onClick={() => updateEmail.mutate({ emailId: email.id, data: { is_primary: true } })}
              disabled={createVerificationRequest.isSuccess}
            />
          )}
          <IconButton
            type="button"
            className="text-danger"
            name="trash"
            disabled={email.is_primary}
            onClick={() => deleteEmail.mutate({ emailId: email.id })}
          />
        </ActionBox>
      </div>
      {createVerificationRequest.isSuccess && <Success>{t("email-verification-sent")}</Success>}
      {createVerificationRequest.isError && (
        <Failure>
          <ErrorDetail error={createVerificationRequest.error.info} />
        </Failure>
      )}
    </div>
  );
}
